import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useModal } from '../../contexts/ModalContext';

const MyServiceRequestsPage = () => {
  const { token, user: currentUser, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { openModal } = useModal();
  const [myRequests, setMyRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchMyRequests = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    setError('');
    try {
      console.log('Fetching my requests with token:', token ? 'Present' : 'Missing');
      const response = await fetch('/api/service-requests/my-requests', {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Failed to fetch your service requests');
      }
      const data = await response.json();
      setMyRequests(data);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching my service requests:", err);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!authLoading && !currentUser) {
      navigate('/login');
    } else if (token) {
      fetchMyRequests();
    }
  }, [authLoading, currentUser, token, navigate, fetchMyRequests]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (e) {
      return 'Invalid Date';
    }
  };
  
  const formatStatus = (status) => {
    if (!status) return 'N/A';
    return status.charAt(0).toUpperCase() + status.slice(1).replace('_',' ');
  };

  if (authLoading || (isLoading && myRequests.length === 0)) {
    return <div className="container mx-auto p-4"><p>Loading your service requests...</p></div>;
  }

  if (error) {
    return <div className="container mx-auto p-4"><p className="text-red-500">Error: {error}</p></div>;
  }

  return (
    <div className="container mx-auto p-4 md:p-8">
      <h1 className="text-2xl md:text-3xl font-bold text-brand-text-dark mb-8">My Service Requests</h1>
      {myRequests.length === 0 ? (
        <div className="text-center py-10">
          <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
          <h3 className="mt-2 text-lg font-medium text-gray-900">No service requests yet</h3>
          <p className="mt-1 text-sm text-gray-500">You haven't made any service requests. Get started by scheduling one today!</p>
          <div className="mt-6">
            <button 
                type="button"
                onClick={() => openModal('scheduleService')}
                className="cta-button"
            >
              Schedule New Service
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {myRequests.map(req => (
            <div key={req.id} className="bg-white shadow-lg rounded-xl p-6 border border-gray-200 hover:shadow-xl transition-shadow duration-300">
              <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-4">
                <h2 className="text-xl font-semibold text-brand-primary mb-2 sm:mb-0">
                  Request ID: <span className="font-normal text-gray-700">{req.id}</span> - {req.serviceType}
                </h2>
                <span 
                  className={`px-3 py-1 text-sm font-semibold rounded-full leading-tight 
                    ${req.status === 'completed' ? 'bg-green-100 text-green-700' : 
                      req.status === 'cancelled' ? 'bg-red-100 text-red-700' : 
                      req.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 
                      req.status === 'confirmed' ? 'bg-blue-100 text-blue-700' : 
                      req.status === 'in_progress' ? 'bg-indigo-100 text-indigo-700' : 
                      'bg-gray-100 text-gray-700'}
                  `}
                >
                  {formatStatus(req.status)}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600 mb-4">
                <div>
                  <p className="font-medium text-gray-800">Requested Date:</p>
                  <p>{formatDate(req.createdAt)}</p>
                </div>
                <div>
                  <p className="font-medium text-gray-800">Contact:</p>
                  <p>{req.contactFullName}</p>
                  <p>{req.contactEmail}</p>
                  <p>{req.contactPhone}</p>
                </div>
                <div>
                  <p className="font-medium text-gray-800">Address:</p>
                  <p>{req.contactStreet}, {req.contactCity}, {req.contactZip}</p>
                </div>
              </div>
              <div className="mb-4">
                <p className="font-medium text-gray-800 text-sm">Description:</p>
                <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md mt-1">{req.description}</p>
              </div>
              {req.technician && (
                <div className="pt-4 border-t border-gray-200">
                  <p className="font-medium text-gray-800 text-sm">Assigned Technician:</p>
                  <p className="text-sm text-gray-600">{req.technician.username} ({req.technician.email})</p>
                </div>
              )}
              {/* Add a Link to view more details if you create a single service request view page */}
              {/* <Link to={`/service-request/${req.id}`} className="text-brand-accent-orange hover:underline text-sm font-medium">View Details</Link> */}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyServiceRequestsPage; 